import { motion } from "framer-motion";
import { Apple, Brain, Briefcase, ChartColumn, CircleCheckBig, GraduationCap, Mic, NotebookPen, PlayCircle, Sparkles, Video } from "lucide-react";
import { Link } from "react-router-dom";
import { apps } from "@/data/apps";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

const staggerContainer = {
  initial: {},
  whileInView: {
    transition: { staggerChildren: 0.1 },
  },
};

const featureCards = [
  {
    icon: <Mic className="w-5 h-5" />,
    title: "One-tap lecture recording",
    description:
      "Capture lectures, discussions, or ideas instantly. ScribeMind records your audio and converts it into structured notes automatically.",
  },
  {
    icon: <Brain className="w-5 h-5" />,
    title: "AI-generated smart notes",
    description:
      "Every lecture is transformed into organized summaries, key points, and structured notes so nothing important gets lost.",
  },
  {
    icon: <NotebookPen className="w-5 h-5" />,
    title: "Automatic tests and quizzes",
    description:
      "Turn class content into practice questions and quizzes that help you retain information faster.",
  },
  {
    icon: <Sparkles className="w-5 h-5" />,
    title: "Learn faster",
    description:
      "Review lectures in minutes instead of hours with instant summaries, key insights, and quick revision tools.",
  },
  {
    icon: <ChartColumn className="w-5 h-5" />,
    title: "Compare and improve",
    description:
      "See how your understanding compares and identify where you need more review.",
  },
];

const steps = [
  {
    number: "1",
    title: "Record your lecture",
    description: "Tap record and capture your lecture, meeting, or discussion.",
  },
  {
    number: "2",
    title: "AI creates notes instantly",
    description: "ScribeMind transcribes and organizes your content automatically.",
  },
  {
    number: "3",
    title: "Review and study",
    description: "Use summaries, quizzes, and structured notes to learn faster.",
  },
];

const audiences = [
  {
    icon: <GraduationCap className="w-5 h-5" />,
    title: "Students",
    description: "Capture lectures and prepare for exams faster.",
  },
  {
    icon: <Video className="w-5 h-5" />,
    title: "Online learners",
    description: "Turn videos and lessons into structured notes.",
  },
  {
    icon: <Briefcase className="w-5 h-5" />,
    title: "Professionals",
    description: "Record meetings, trainings, and workshops.",
  },
  {
    icon: <Brain className="w-5 h-5" />,
    title: "Lifelong learners",
    description: "Capture ideas and convert them into knowledge.",
  },
];

const benefits = [
  "Save hours of note-taking",
  "Never miss important ideas",
  "Understand lectures faster",
  "Study with built-in quizzes",
  "Organize everything in one place",
];

const pricingPackages = [
  "1 Hour Lecture Time",
  "5 Hour Package",
  "10 Hour Package",
];

const scribemindApp = apps.find((app) => app.id === "scribemind");

const ScribeMindPage = () => {
  return (
    <div className="min-h-screen pt-20 bg-white" data-testid="scribemind-page">
      <section className="min-h-[calc(100vh-5rem)] flex items-center py-16 md:py-24">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-16 items-center"
          >
            <div className="max-w-4xl">
              <p className="caption mb-4">ScribeMind</p>
              <h1 className="heading-1 mb-6">
                Turn lectures into {" "}
                <span className="bg-gradient-to-r from-purple-600 via-fuchsia-500 to-pink-500 bg-clip-text text-transparent">
                  smart notes in seconds.
                </span>
              </h1>
              <p className="body-lg max-w-3xl mb-6">
                Record your lecture. Let AI do the rest.
              </p>
              <p className="body-text max-w-2xl mb-8">
                ScribeMind transforms lectures, ideas, and conversations into structured
                notes, summaries, and quizzes instantly, helping you study faster and
                remember more.
              </p>
              <div className="flex flex-wrap gap-3 text-sm font-medium text-zinc-600 mb-10">
                <span className="rounded-full bg-zinc-100 px-4 py-2">Record</span>
                <span className="rounded-full bg-zinc-100 px-4 py-2">Understand</span>
                <span className="rounded-full bg-zinc-100 px-4 py-2">Master</span>
              </div>
              {scribemindApp?.appStoreUrl && (
                <div className="flex flex-wrap gap-4">
                  <a
                    href={scribemindApp.appStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary inline-flex items-center justify-center gap-2"
                    data-testid="scribemind-hero-download"
                  >
                    <Apple className="w-4 h-4" />
                    Download on iPhone
                  </a>
                  <Link
                    to="/privacy/scribemind"
                    className="btn-secondary inline-flex items-center justify-center"
                    data-testid="scribemind-privacy-link"
                  >
                    Privacy Policy
                  </Link>
                </div>
              )}
            </div>

            <motion.div
              {...fadeInUp}
              className="relative rounded-[2.5rem] bg-[#F5F5F7] border border-zinc-100 p-8 md:p-10 overflow-hidden"
            >
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(217,70,239,0.14),transparent_35%),radial-gradient(circle_at_bottom_left,rgba(147,51,234,0.12),transparent_30%)]" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-zinc-700 shadow-sm">
                  <PlayCircle className="w-4 h-4 text-fuchsia-500" />
                  Study smarter, not harder
                </div>
                <div className="mt-8 space-y-4">
                  <div className="rounded-[1.75rem] bg-white p-5 shadow-[0_10px_30px_rgba(0,0,0,0.04)]">
                    <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-3">Lecture captured</p>
                    <p className="text-base font-medium text-zinc-900">Behavioral Economics, Week 4</p>
                    <p className="text-sm text-zinc-500 mt-2">42 min recording processed automatically</p>
                  </div>
                  <div className="rounded-[1.75rem] bg-white p-5 shadow-[0_10px_30px_rgba(0,0,0,0.04)]">
                    <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-3">Generated for you</p>
                    <div className="space-y-2 text-sm text-zinc-600">
                      <p>Structured summary</p>
                      <p>Key concepts and action points</p>
                      <p>Quiz questions for revision</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="section-spacing bg-[#F5F5F7]">
        <div className="container-main">
          <motion.div {...fadeInUp} className="max-w-3xl mx-auto text-center mb-16">
            <p className="caption mb-4">What Is ScribeMind?</p>
            <h2 className="heading-2 mb-6">Your AI-powered study partner.</h2>
            <div className="space-y-4 body-text">
              <p>
                Studying should not mean trying to listen, write, and understand at the
                same time.
              </p>
              <p>
                ScribeMind captures your lecture, turns it into organized notes, and
                helps you test your knowledge automatically so you can focus on learning
                and mastering the material.
              </p>
            </div>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {audiences.map((audience) => (
              <motion.div key={audience.title} variants={fadeInUp} className="feature-card text-center">
                <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center mx-auto mb-5 text-zinc-600">
                  {audience.icon}
                </div>
                <h3 className="text-xl font-medium text-zinc-900 mb-3">{audience.title}</h3>
                <p className="text-sm leading-relaxed text-zinc-600">{audience.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="section-spacing bg-white">
        <div className="container-main">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto mb-16">
            <p className="caption mb-4">Key Features</p>
            <h2 className="heading-2 mb-6">Built for better learning.</h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
          >
            {featureCards.map((feature) => (
              <motion.div key={feature.title} variants={fadeInUp} className="feature-card">
                <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-600 mb-5">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-medium text-zinc-900 mb-3">{feature.title}</h3>
                <p className="text-sm leading-relaxed text-zinc-600">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="section-spacing bg-[#F5F5F7]">
        <div className="container-main">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto mb-16">
            <p className="caption mb-4">How It Works</p>
            <h2 className="heading-2 mb-6">From lecture to understanding.</h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="grid grid-cols-1 lg:grid-cols-3 gap-6"
          >
            {steps.map((step) => (
              <motion.div key={step.number} variants={fadeInUp} className="feature-card">
                <p className="text-sm font-semibold tracking-[0.2em] text-zinc-400 mb-5">{step.number}</p>
                <h3 className="heading-3 mb-4">{step.title}</h3>
                <p className="body-text">{step.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="section-spacing bg-white">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <motion.div {...fadeInUp}>
              <p className="caption mb-4">Why Students Love ScribeMind</p>
              <h2 className="heading-2 mb-6">Information into understanding.</h2>
              <div className="space-y-4">
                {benefits.map((benefit) => (
                  <div key={benefit} className="flex items-start gap-3">
                    <CircleCheckBig className="w-5 h-5 mt-1 text-zinc-500 flex-shrink-0" />
                    <p className="body-text">{benefit}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div {...fadeInUp} className="feature-card">
              <p className="caption mb-4">Who It&apos;s For</p>
              <h3 className="heading-3 mb-6">Learn anywhere you listen.</h3>
              <div className="space-y-5">
                {audiences.map((audience) => (
                  <div key={audience.title} className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-600 flex-shrink-0">
                      {audience.icon}
                    </div>
                    <div>
                      <p className="font-medium text-zinc-900">{audience.title}</p>
                      <p className="text-sm text-zinc-600 mt-1">{audience.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section-spacing bg-[#F5F5F7]">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <motion.div {...fadeInUp} className="feature-card">
              <p className="caption mb-4">Simple Pricing</p>
              <h2 className="heading-3 mb-4">Pay only for what you use.</h2>
              <p className="body-text mb-6">
                ScribeMind lets you purchase lecture time as you need it. Flexible
                learning without subscriptions.
              </p>
              <div className="space-y-3">
                {pricingPackages.map((pkg) => (
                  <div key={pkg} className="rounded-2xl bg-white border border-zinc-100 px-5 py-4 text-zinc-700">
                    {pkg}
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div {...fadeInUp} className="feature-card">
              <p className="caption mb-4">Download ScribeMind</p>
              <h2 className="heading-3 mb-4">Record. Learn. Remember.</h2>
              <p className="body-text mb-8">
                Your AI study partner is ready. Download ScribeMind today and start
                learning faster.
              </p>
              {scribemindApp?.appStoreUrl && (
                <div className="flex flex-wrap gap-4">
                  <a
                    href={scribemindApp.appStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary inline-flex items-center justify-center gap-2"
                    data-testid="scribemind-download-section-cta"
                  >
                    <Apple className="w-4 h-4" />
                    Download on the App Store
                  </a>
                  <Link
                    to="/privacy/scribemind"
                    className="btn-secondary inline-flex items-center justify-center"
                    data-testid="scribemind-download-privacy-link"
                  >
                    Privacy Policy
                  </Link>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default ScribeMindPage;
